import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { useAuthProvider } from "ra-core";
import { useMemo } from "react";

import type { NavAccess, NavGroup, NavItem } from "./navigation";

const ACCESS_STALE_MS = 5 * 60 * 1000;

function accessKey(access: NavAccess): string {
  return `${access.resource}:${access.action}`;
}

/**
 * Filters navigation items down to the ones the signed-in user may open.
 *
 * One query per distinct (resource, action) gate — a dozen items share three
 * gates — instead of a `<CanAccess>` wrapper per item, so a whole "More" group
 * can be hidden when none of its entries survive. Gated items stay hidden while
 * their first check is pending (no flash of admin links); without an auth
 * provider, as in the demo and in tests, everything is visible.
 *
 * A check that *fails* (Supabase could not identify the operator) is retried
 * with backoff and keeps the last known answer meanwhile, so a transient
 * outage neither hides the administrator tabs nor caches a denial.
 */
export function useAccessibleNavItems(
  items: readonly NavItem[],
): readonly NavItem[] {
  const authProvider = useAuthProvider();
  const canAccess = authProvider?.canAccess;

  const gates = useMemo(() => {
    const byKey = new Map<string, NavAccess>();
    for (const item of items) {
      if (item.access) byKey.set(accessKey(item.access), item.access);
    }
    return Array.from(byKey.values());
  }, [items]);

  const results = useQueries({
    queries: gates.map((gate) => ({
      queryKey: ["navigation", "can-access", gate.resource, gate.action],
      queryFn: async () =>
        canAccess
          ? canAccess({ resource: gate.resource, action: gate.action })
          : true,
      staleTime: ACCESS_STALE_MS,
      retry: 3,
      retryDelay: (attempt: number) => Math.min(500 * 2 ** attempt, 4000),
      placeholderData: keepPreviousData,
    })),
  });

  return useMemo(() => {
    const allowed = new Map<string, boolean>();
    gates.forEach((gate, index) => {
      const result = results[index];
      allowed.set(accessKey(gate), canAccess ? result?.data === true : true);
    });
    return items.filter(
      (item) => !item.access || allowed.get(accessKey(item.access)) === true,
    );
    // `results` is a fresh array each render; its `data` values are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, gates, canAccess, ...results.map((result) => result.data)]);
}

/** Same filter applied group by group, dropping groups left empty. */
export function useAccessibleNavGroups(
  groups: readonly NavGroup[],
): readonly NavGroup[] {
  const allItems = useMemo(
    () => groups.flatMap((group) => group.items),
    [groups],
  );
  const accessible = useAccessibleNavItems(allItems);
  return useMemo(() => {
    const allowedIds = new Set(accessible.map((item) => item.id));
    return groups
      .map((group) => ({
        ...group,
        items: group.items.filter((item) => allowedIds.has(item.id)),
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, accessible]);
}
